import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Search, Shapes, X } from 'lucide-react'
import { loadLucideIcons } from '../primitives/lucideRegistry'
import { ICON_GROUPS, iconLabel, matchesIcon, searchTerms } from './iconCatalog'
import { useScrimDismiss } from '../../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../../shared/hooks/useEscapeClose'
import { hasFieldValue } from './fieldValue'

/**
 * La bibliothèque d'icônes — pop-in plein écran de sélection d'icône du builder.
 *
 * Remplace le menu déroulant de 260 px qui ne montrait que 54 icônes curatées :
 * on y parcourt le SET LUCIDE COMPLET (~1700), rangé par thèmes et cherchable
 * en français (cf. `iconCatalog.js`).
 *
 * Pourquoi une pop-in plein écran alors que la règle du builder est « édition
 * en place, pas de modale » : parcourir une bibliothèque n'est pas modifier un
 * champ. C'est exactement le statut de la médiathèque et du catalogue de blocs,
 * dont elle reprend la surface (`modal-panel-full`) et le rail de facettes.
 *
 * Trois points structurants :
 *  - le set complet arrive par un import dynamique (`loadLucideIcons`) : rien
 *    n'entre dans le téléchargement initial de l'application ;
 *  - la grille se remplit par tranches (`PAGE`) au fil du défilement — rendre
 *    1700 SVG d'un coup fige la fenêtre une seconde ;
 *  - le portail vers `<body>` + `modal-scrim-full` (z-index 1000) est
 *    obligatoire : un bloc de la page peut déclarer un z-index et transpercer
 *    une modale rendue en place.
 *
 * Props :
 *  - value   : le nom Lucide actuellement stocké (ou null)
 *  - onPick  : (name) => void — l'appelant décide de ce qu'il stocke
 *  - onClear : () => void — RETIRER l'icône du champ (facultatif). Comme la
 *              médiathèque, cette bibliothèque ne sait que choisir : sans
 *              cette commande, une icône posée une fois ne se reprend plus.
 *  - onClose : () => void
 *  - title   : intitulé de la pop-in
 */

/** Icônes ajoutées à la grille par tranche (au premier rendu, puis au défilement). */
const PAGE = 180

const ALL = 'all'

export const IconLibrary = ({ value, onPick, onClear = null, onClose, title = 'Bibliothèque d’icônes' }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)

  const [iconSet, setIconSet] = useState(null)   // carte nom → composant, ou null tant qu'elle charge
  const [search, setSearch] = useState('')
  // On ouvre sur le thème qui contient déjà l'icône du champ : le rédacteur voit
  // d'abord son voisinage, pas le début de l'alphabet.
  const [group, setGroup] = useState(
    () => ICON_GROUPS.find((g) => g.icons.some((i) => i.name === value))?.key ?? ALL,
  )
  const [selected, setSelected] = useState(value || null)
  const [visible, setVisible] = useState(PAGE)

  const searchRef = useRef(null)
  const gridRef = useRef(null)
  const sentinelRef = useRef(null)
  const selectedTileRef = useRef(null)

  useEffect(() => {
    let alive = true
    loadLucideIcons().then((map) => { if (alive) setIconSet(map) })
    return () => { alive = false }
  }, [])

  useEffect(() => { searchRef.current?.focus() }, [])

  // Le focus revient au bouton qui a ouvert la pop-in. Sans cela il repart au
  // <body> (le panneau est monté dans un portail) et la navigation clavier
  // recommence en haut de page — d'autant plus visible depuis que le choix
  // d'un picto passe par ici, hors builder.
  const openerRef = useRef(typeof document === 'undefined' ? null : document.activeElement)
  useEffect(() => () => { openerRef.current?.focus?.() }, [])

  // Une recherche porte sur TOUT le set : chercher « camion » depuis le thème
  // « Interface » doit trouver le camion, pas rendre une grille vide.
  const searching = search.trim().length > 0
  const terms = useMemo(() => searchTerms(search), [search])

  const names = useMemo(() => {
    if (!iconSet) return []
    const pool = searching || group === ALL
      ? Object.keys(iconSet)
      : (ICON_GROUPS.find((g) => g.key === group)?.icons ?? []).map((i) => i.name)
    return pool.filter((name) => iconSet[name] && matchesIcon(name, terms))
  }, [iconSet, group, searching, terms])

  // Remonter en haut et repartir d'une tranche à chaque changement de vue.
  useEffect(() => {
    setVisible(PAGE)
    gridRef.current?.scrollTo?.({ top: 0 })
  }, [group, search])

  // Le défilement remplit la grille par tranches ; la sentinelle est le dernier
  // élément du flux, elle n'apparaît donc que quand la tranche est parcourue.
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || visible >= names.length) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setVisible((n) => n + PAGE) },
      { root: gridRef.current, rootMargin: '400px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [visible, names.length])

  // L'icône déjà posée doit être VISIBLE à l'ouverture, même si elle est la
  // 900e du set : on déplie assez de tranches pour l'atteindre, puis on l'amène
  // dans le cadre.
  const selectedIndex = selected ? names.indexOf(selected) : -1
  useLayoutEffect(() => {
    if (selectedIndex < 0) return
    if (selectedIndex >= visible) { setVisible(Math.ceil((selectedIndex + 1) / PAGE) * PAGE); return }
    selectedTileRef.current?.scrollIntoView({ block: 'center' })
    // Une seule fois par vue : la dépendance est l'index, pas la sélection en
    // cours — cliquer une icône ne doit pas faire sauter la grille.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconSet, group, search])

  const shown = names.slice(0, visible)
  // L'intitulé français ne remplace le nom Lucide que dans un THÈME : dans le
  // set complet, une poignée de cases légendées « Port » ou « Suivant » au
  // milieu de 1700 noms anglais donne à croire à deux nomenclatures.
  const namedByTheme = !searching && group !== ALL

  return createPortal(
    <div className="modal-scrim modal-scrim-full" {...scrim}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="icon-library-title"
        className="modal-panel modal-panel-full icon-lib"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ── En-tête : titre, recherche, fermeture ── */}
        <div className="media-lib-head">
          <h2 id="icon-library-title" className="media-lib-title">
            <Shapes size={17} aria-hidden="true" />
            {title}
          </h2>

          <div className="media-lib-search">
            <Search size={15} aria-hidden="true" className="media-lib-search-icon" />
            <input
              ref={searchRef}
              id="icon-library-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une icône…"
              aria-label="Rechercher une icône par son nom ou par un mot-clé français"
              className="media-lib-search-input"
            />
          </div>

          <button
            type="button"
            onClick={onClose}
            className="media-lib-close"
            aria-label="Fermer la bibliothèque d'icônes"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <p aria-live="polite" className="sr-only">
          {iconSet ? `${names.length} icône${names.length > 1 ? 's' : ''} disponible${names.length > 1 ? 's' : ''}.` : 'Chargement des icônes…'}
        </p>

        {/* ── Corps : rail de thèmes | grille ── */}
        <div className="icon-lib-body">
          <nav className="facet-rail" aria-label="Thèmes d'icônes">
            <button
              type="button"
              onClick={() => { setSearch(''); setGroup(ALL) }}
              aria-current={group === ALL && !searching ? 'true' : undefined}
              className="facet-rail-item"
            >
              <span>Toutes les icônes</span>
              <span className="facet-rail-count">{iconSet ? Object.keys(iconSet).length : '…'}</span>
            </button>

            <p className="facet-rail-heading">Thèmes</p>
            {ICON_GROUPS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => { setSearch(''); setGroup(g.key) }}
                aria-current={group === g.key && !searching ? 'true' : undefined}
                className="facet-rail-item"
              >
                <span>{g.label}</span>
                <span className="facet-rail-count">{g.icons.length}</span>
              </button>
            ))}
          </nav>

          <div ref={gridRef} className="media-lib-main">
            {!iconSet && (
              <p className="media-lib-empty">
                <Loader2 size={22} className="animate-spin" aria-hidden="true" />
                Chargement des icônes…
              </p>
            )}

            {iconSet && names.length === 0 && (
              <p className="media-lib-empty">
                Aucune icône ne correspond à « {search} ».
                <span className="media-lib-empty-hint">
                  Les icônes sont nommées en anglais : essayez « arrow », « user », « chart »…
                </span>
              </p>
            )}

            {iconSet && names.length > 0 && (
              <>
                <ul className="icon-lib-grid">
                  {shown.map((name) => {
                    const Icon = iconSet[name]
                    const label = iconLabel(name)
                    const isSelected = selected === name
                    return (
                      <li key={name}>
                        <button
                          ref={isSelected ? selectedTileRef : null}
                          type="button"
                          onClick={() => setSelected(name)}
                          onDoubleClick={() => { onPick(name); onClose() }}
                          aria-pressed={isSelected}
                          data-selected={isSelected ? 'true' : undefined}
                          title={label ? `${label} (${name})` : name}
                          className="icon-lib-tile"
                        >
                          <Icon size={24} aria-hidden="true" />
                          <span className="icon-lib-tile-name">{namedByTheme ? (label ?? name) : name}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
                {visible < names.length && (
                  <div ref={sentinelRef} className="media-lib-more">
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Pied : l'icône retenue, puis validation ── */}
        <div className="modal-footer">
          <p className="icon-lib-pick">
            {selected && iconSet?.[selected]
              ? (() => {
                  const Icon = iconSet[selected]
                  return (
                    <>
                      <span className="icon-lib-pick-preview"><Icon size={22} aria-hidden="true" /></span>
                      <span>
                        {iconLabel(selected) ?? selected}
                        <code className="icon-lib-pick-name">{selected}</code>
                      </span>
                    </>
                  )
                })()
              : <span className="icon-lib-pick-empty">Aucune icône sélectionnée</span>}
          </p>
          {onClear && hasFieldValue(value) && (
            <button
              type="button"
              onClick={() => { onClear(); onClose() }}
              className="btn btn-ghost"
            >
              Retirer l’icône
            </button>
          )}
          <button type="button" onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button
            type="button"
            disabled={!selected}
            onClick={() => { if (selected) { onPick(selected); onClose() } }}
            className="btn btn-primary"
          >
            Utiliser cette icône
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
